import { Box, Grid, Paper, Typography, useTheme, Skeleton, Button, IconButton, Select, MenuItem, FormControl } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import routing from "../../routing";
import SegmentedTabs from "../../components/tabs/SegmentedTabs";
import SubTabs from "../../components/tabs/SubTabs";
import BuildingCard from "./components/BuildingCard";
import { fetchWithToken } from "../../authentication/AuthProvider";
import config from "../../config";
import { LuFactory, LuTruck, LuZap, LuFootprints, LuBox, LuActivity, LuInfo, LuHammer, LuLightbulb, LuTrash2, LuRefreshCw } from "react-icons/lu";
import { FiCopy, FiLayers, FiTrash2, FiCheckCircle } from "react-icons/fi";
import { v4 as uuidv4 } from "uuid";
import Right_Foot from "../../assets/logo/right_foot.png";
import { useAuth } from "../../authentication/AuthProvider";
import AddButton from "../../components/button/AddButton";
import ColumnVisibilityButton from "../../components/button/ColumnVisibilityButton";
import HideButton from "../../components/button/HideButton";
import RefreshButton from "../../components/button/RefreshButton";
import SaveButton from "../../components/button/SaveButton";
import UnhideButton from "../../components/button/UnhideButton";
import { PageTitleContainer } from "../../components/container/PageTitleContainer";
import ResizableBox from "../../components/container/ResizableBox";
import { TableHeaderContainer } from "../../components/container/TableHeaderContainer";
import { TableTitleContainer } from "../../components/container/TableTitleContainer";
import ColumnMenu from "../../components/context-menu/ColumnMenu";
import ContextMenu from "../../components/context-menu/ContextMenu";
import DialogDelete from "../../components/dialog/DialogDelete";
import UnhideDialog from "../../components/dialog/UnhideDialog";
import ExportAsXLSXButton from "../../components/export/ExportAsXLSXButton";
import AgGrid, { GridPagination } from "../../components/grid/AgGrid";
import { centerAlignColDef, checkBoxColDef, defaultColDef, floatFormater, leftAlignColDef, rightAlignColDef } from "../../components/grid/columnDefinitions/columnDefinitions";
import CheckBox from "../../components/input/CheckBox";
import SearchBox from "../../components/input/SearchBox";
import { useContextProvider } from "../../components/page-context/ContextProvider";
import { Building } from "../../db_access/building";
import "../../style/pages-style/Utilizer.css";
import "../../style/pages-style/buildings/BuildingDashboard.css";
import "../../style/pages-style/Organization/organizationDashboard.css";
import { useColorMode } from "../../theme/ThemeProviderWrapper";
import CardTotalGwp from "../Products/Dashboard/CardTotalGwp";
import CardTotalGwpStage from "../Products/Dashboard/CardTotalGwpStage";

import MetricCard from "../../app_components/MetricCard";

import GlobalViewContainer from "./GlobalView/GlobalViewContainer";
import { TableFooterCheckBoxesContainer } from "../../components/container/TableFooterCheckBoxesContainer";
import { TableFooterContainer } from "../../components/container/TableFooterContainer";
import { TableHeaderToolsContainer } from "../../components/container/TableHeaderToolsContainer";
import { useBuildingAggregatedData, useBuildingsDashboard, useHiddenBuildings, useInvalidateBuildingCache } from "../../utilities/useBuildingQueries";
import CardsContainer from "../../components/container/CardsContainer";
import { getOrgTokens, ORG_PALETTE } from "../../theme/style/organizationPalette";
import { MainContainer } from "../../components/container/MainContainer";

const METRIC_CONFIG = [
  { key: "total", title: "Total Carbon Footprint", icon: LuFootprints, accent: "#16a34a", bg: "rgba(22, 163, 74, 0.12)", unit: "t CO₂e" },
  { key: "material", title: "Material Impact", icon: LuBox, accent: "#2563eb", bg: "rgba(37, 99, 235, 0.12)", unit: "t CO₂e" },
  { key: "transport", title: "Transport Emissions", icon: LuTruck, accent: "#d97706", bg: "rgba(217, 119, 6, 0.12)", unit: "t CO₂e" },
  { key: "energy", title: "Energy Impact", icon: LuZap, accent: "#7c3aed", bg: "rgba(124, 58, 237, 0.12)", unit: "t CO₂e" },
];

const STAGE_CONFIG = [
  { key: "A1-A3", title: "A1 - A3", subtitle: "Manufacture", icon: LuFactory, accent: "#0891b2", bg: "rgba(8, 145, 178, 0.1)", unit: "t CO₂e" },
  { key: "A4-A5", title: "A4 - A5", subtitle: "Installation", icon: LuHammer, accent: "#10b981", bg: "rgba(16, 185, 129, 0.1)", unit: "t CO₂e" },
  { key: "B1-B5", title: "B1 - B5", subtitle: "Maintenance", icon: LuActivity, accent: "#f59e0b", bg: "rgba(245, 158, 11, 0.1)", unit: "t CO₂e" },
  { key: "B6-B7", title: "B6 - B7", subtitle: "Operational", icon: LuLightbulb, accent: "#6366f1", bg: "rgba(99, 102, 241, 0.1)", unit: "t CO₂e" },
  { key: "C", title: "C", subtitle: "Disposal", icon: LuTrash2, accent: "#ef4444", bg: "rgba(239, 68, 68, 0.1)", unit: "t CO₂e" },
  { key: "D", title: "D", subtitle: "Reuse", icon: LuRefreshCw, accent: "#8b5cf6", bg: "rgba(139, 92, 246, 0.1)", unit: "t CO₂e" },
];

// Building categories list
const buildingCategories = [
  "Education",
  "Food Sales",
  "Food Service",
  "Health Care",
  "Lodging",
  "Mercantile / Retail",
  "Office",
  "Public Assembly",
  "Public Order and Safety",
  "Religious Worship",
  "Service",
  "Warehouse and Storage",
  "Residential",
  "Industrial / Utility",
  "Other",
  "Vacant",
];

const CompactStageCard = ({ title, subtitle, value, icon: Icon, accent, bg, delay, isDark, tok, forceTons }) => {
  const displayValue = useMemo(() => {
    if (typeof value !== 'number') return value;
    if (value >= 100 || forceTons) {
      const valInTons = value / 1000;
      return valInTons.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' t CO₂e';
    }
    return Math.round(value).toLocaleString() + ' kg CO₂e';
  }, [value, forceTons]);

  return (
    <Box
      sx={{
        animationDelay: `${delay}s`,
        background: bg || tok.bg.metricDefault,
        p: '12px 14px',
        borderRadius: tok.radius.card,
        border: '1px solid',
        borderColor: tok.border.card,
        minHeight: '80px',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: '12px',
        flex: 1,
        minWidth: 0,
        boxShadow: tok.shadow.card,
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
            transform: ORG_PALETTE.animation.cardHover,
            boxShadow: tok.shadow.cardHover
        }
      }}
    >
      {Icon && (
        <Box sx={{
          background: isDark ? 'rgba(255,255,255,0.08)' : '#fff',
          width: 36,
          height: 36,
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: tok.shadow.iconBox,
          flexShrink: 0
        }}>
          <Icon size={18} color={accent} />
        </Box>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minWidth: 0, gap: '4px', alignItems: 'flex-start', justifyContent: 'center' }}>
        <Typography sx={{
          fontSize: '11px',
          fontWeight: 600,
          color: isDark ? tok.text.muted : '#64748b',
          letterSpacing: '0.02em',
          lineHeight: 1.2,
          textAlign: 'left',
          width: '100%',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>
          {title} - {subtitle}
        </Typography>

        <Typography sx={{
          fontSize: '18px',
          fontWeight: 700,
          color: isDark ? '#ffffff' : '#0f172a',
          lineHeight: 1.1,
          textAlign: 'left',
          width: '100%',
          whiteSpace: 'nowrap'
        }}>
          {displayValue}
        </Typography>
      </Box>
    </Box>
  );
};



const MURB_TEMPLATES = [
  { id: "murb_ext_win", name: "MURB Exterior Windows", building_category: "Building Component", location: "Global", gwp: 6.65 },
  { id: "murb_floor_const", name: "MURB Floor Construction", building_category: "Building Component", location: "Global", gwp: 116.02 },
  { id: "murb_int_doors", name: "MURB Interior Doors", building_category: "Building Component", location: "Global", gwp: 2.29 },
  { id: "murb_int_wall", name: "MURB Interior Wall", building_category: "Building Component", location: "Global", gwp: 18.93 },
  { id: "murb_roof_const", name: "MURB Roof Construction", building_category: "Building Component", location: "Global", gwp: 9.85 },
  { id: "murb_roof_cov", name: "MURB Roof Covering", building_category: "Building Component", location: "Global", gwp: 0.40 },
  { id: "murb_roof_open", name: "MURB Roof Opening", building_category: "Building Component", location: "Global", gwp: 0.02 },
  { id: "murb_slab_grade", name: "MURB Slab on Grade", building_category: "Building Component", location: "Global", gwp: 77.99 },
  { id: "murb_spec_found", name: "MURB Special Foundation", building_category: "Building Component", location: "Global", gwp: 0.63 },
  { id: "murb_stair_const", name: "MURB Stair Construction", building_category: "Building Component", location: "Global", gwp: 1.67 },
  { id: "murb_std_found", name: "MURB Standard Foundation", building_category: "Building Component", location: "Global", gwp: 14.03 },
];

const BuildingDashboard = () => {
  const {
    selectedBuildings,
    setSelectedBuildings,
    setSelectedBuildingIds,
    refresh,
    setRefresh,
    columnVisibilities,
    isSaveClicked,
    setIsSaveVisible,
    setIsLoading,
    isLoading,
    setIsSaveClicked,
    enqueueAlert,
  } = useContextProvider();

  const { t } = useTranslation();
  const { mode } = useColorMode();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const { invalidateAll } = useInvalidateBuildingCache();
  const [activeMainTab, setActiveMainTab] = useState("Dashboard");
  const [activeSubTab, setActiveSubTab] = useState("Dashboard");
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [dashboardView, setDashboardView] = useState("Grid View"); // "Grid View" or "Card View"
  const [templateView, setTemplateView] = useState("Card View"); // "Card View" or "Grid View"
  const [validationSubTab, setValidationSubTab] = useState("Pending"); // "Pending" or "Validated"
  const [validationView, setValidationView] = useState("Grid View"); // "Grid View" or "Card View"
  const [buildingImageCache, setBuildingImageCache] = useState({});
  const buildingImageCacheRef = useRef({});

  const resolveBuildingImageFromRow = useCallback((building) => {
    const candidates = [
      building?.photo_url,
      building?.photo,
      building?.image_url,
      building?.image,
      building?.thumbnail,
      building?.building_photo,
      building?.workspace_photo,
      building?.workspace_photo_url,
    ];

    for (const value of candidates) {
      if (!value) continue;
      if (typeof value === "string" && value.trim()) return value;
      if (typeof value === "object") {
        if (typeof value.url === "string" && value.url.trim()) return value.url;
        if (typeof value.secure_url === "string" && value.secure_url.trim()) return value.secure_url;
        if (typeof value.path === "string" && value.path.trim()) return value.path;
      }
    }

    return "";
  }, []);

  useEffect(() => {
    setSelectedTemplateId(null);
  }, [activeSubTab]);

  const handleCardMenuClick = (event, building) => {
    event.preventDefault();
    event.stopPropagation();
    setSelectedBuildings([building]);
    setSelectedBuildingIds([building.id]);
    setMenuPosition({ x: event.clientX, y: event.clientY });
    setMenuVisible(true);
  };

  const handleOpenBuilding = (building) => {
    setSelectedBuildings([building]);
    setSelectedBuildingIds([building.id]);
    navigate(routing.sector.buildings.workspace);
  };

  const handleUseTemplate = async (template) => {
    const baseMurb = buildings.find(b => b.name?.includes("MURB") && b.is_published_template);
    const baseId = baseMurb ? baseMurb.id : "8b80826937504d5fb3f3f6ac6af44ac2";

    try {
      setIsLoading(true);
      const res = await fetchWithToken(config.building.duplicate_from_template, {
        ...config.method.POST,
        headers: config.header.json,
        body: JSON.stringify({ ids: [baseId] }),
      });
      const duplicatedIds = await res.json();
      const newId = duplicatedIds[0];

      const fd = new FormData();
      fd.append("id", newId);
      fd.append("name", template.name);
      fd.append("functional_unit", "m2");
      fd.append("other_unit", "");
      fd.append("scope", "");
      fd.append("goals", "");
      fd.append("assumptions", "");
      fd.append("system_boundary", "");

      await fetch(config.building.root + "/save_scope_and_goals", {
        method: "POST",
        headers: config.header.authorization(JSON.parse(localStorage.getItem("user")).access_token),
        body: fd
      });

      enqueueAlert({ message: t("Building template created in Dashboard"), severity: "success" });
      
      invalidateAll();
      setRefresh(r => !r);
      setActiveSubTab("Dashboard");
    } catch (e) {
      console.error(e);
      enqueueAlert({ message: t("Failed to duplicate template"), severity: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const colVisibility_key = "building_dashboard_table_column_visibility";
  const defaultCols = ["name", "building_category"];
  const defaultNewBuilding = { year: new Date().getFullYear() };
  const gridRef = useRef(null);
  const dialogRef = useRef(null);
  const gridHeaderContainerRef = useRef();

  const [buildings, setBuildings] = useState([]);
  const [focusedBuilding, setFocusedBuilding] = useState();
  const [menuVisible, setMenuVisible] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const [globalView, setGlobalView] = useState(false);
  const [searchText, setSearchText] = useState("");

  const [columnMenuVisible, setColumnMenuVisible] = useState(false);
  const [actionQueue, setActionQueue] = useState([]);
  const [hiddenBuildings, setHiddenBuildings] = useState([]);
  const [unhideDialogOpen, setUnhideDialogOpen] = useState(false);
  const [globalViewType, setGlobalViewType] = useState("all"); // "all" or "category"

  useEffect(() => {
    buildingImageCacheRef.current = buildingImageCache;
  }, [buildingImageCache]);

  useEffect(() => {
    return () => {
      for (const url of Object.values(buildingImageCacheRef.current)) {
        if (typeof url === "string" && url.startsWith("blob:")) {
          URL.revokeObjectURL(url);
        }
      }
    };
  }, []);

  // AGGREGATED CARD DATA — only called when the user has explicitly selected buildings.
  // When nothing is selected, show zero values derived from local list sum (no API call).
  // This prevents get_global_view_data from firing on every name edit or delete.
  const buildingIdsForAggregation = useMemo(() => {
    if (selectedBuildings.length === 0) return [];
    const unsavedIds = new Set(
      actionQueue.filter((a) => a.action === "new").map((a) => a.item.id)
    );
    return selectedBuildings.filter((b) => !unsavedIds.has(b.id)).map((b) => b.id).sort();
  }, [selectedBuildings, actionQueue]);

  // CACHED: Aggregated data from TanStack Query (enabled only when IDs are present)
  const { data: cachedAggregatedData, isLoading: isAggregationLoading } = useBuildingAggregatedData(
    buildingIdsForAggregation.length > 0 ? buildingIdsForAggregation : []
  );

  // Use cached aggregated data; when nothing is selected fall back to local sum
  const aggregatedData = useMemo(() => {
    if (cachedAggregatedData) return cachedAggregatedData;
    if (selectedBuildings.length === 0) {
      // Derive a rough total from the locally cached buildings list (no API)
      const localGwp = buildings.reduce((sum, b) => sum + (b.gwp || 0), 0);
      return {
        totalGwp: localGwp,
        stages: { "A1-A3": 0, "A4-A5": 0, "B1-B5": 0, "B6-B7": 0, C: 0, D: 0 },
        types: { Material: 0, Transport: 0, Energy: 0 },
      };
    }
    return {
      totalGwp: 0,
      stages: { "A1-A3": 0, "A4-A5": 0, "B1-B5": 0, "B6-B7": 0, C: 0, D: 0 },
      types: { Material: 0, Transport: 0, Energy: 0 },
    };
  }, [cachedAggregatedData, selectedBuildings.length, buildings]);

  const selectedTemplate = useMemo(() => {
    if (activeSubTab !== "Template Library" || !selectedTemplateId) return null;
    return MURB_TEMPLATES.find((t) => t.id === selectedTemplateId);
  }, [selectedTemplateId, activeSubTab]);

  const gwpMetrics = useMemo(() => {
    if (selectedTemplate) {
      const g = selectedTemplate.gwp || 0;
      return {
        total: g,
        material: g * 0.8,
        transport: g * 0.2,
        energy: 0,
      };
    }
    return {
      total: aggregatedData.totalGwp,
      material: aggregatedData.types?.Material || 0,
      transport: aggregatedData.types?.Transport || 0,
      energy: aggregatedData.types?.Energy || 0,
    };
  }, [selectedTemplate, aggregatedData]);

  const stageMetrics = useMemo(() => {
    if (selectedTemplate) {
      const g = selectedTemplate.gwp || 0;
      return {
        "A1-A3": g * 0.75,
        "A4-A5": g * 0.25,
        "B1-B5": 0,
        "B6-B7": 0,
        "C": 0,
        "D": 0,
      };
    }
    return aggregatedData.stages || {};
  }, [selectedTemplate, aggregatedData]);

  const anyMetricReachesTons = useMemo(() => {
    return Object.values(gwpMetrics).some((val) => typeof val === "number" && val >= 1000);
  }, [gwpMetrics]);

  const anyStageReachesTons = useMemo(() => {
    return Object.values(stageMetrics).some((val) => typeof val === "number" && val >= 100);
  }, [stageMetrics]);

  // CACHED: Dashboard data from TanStack Query
  const { data: cachedBuildings, refetch: refetchBuildings } = useBuildingsDashboard();
  const { data: cachedHiddenBuildings = [] } = useHiddenBuildings();
  const { invalidateDashboard } = useInvalidateBuildingCache();

  // Sync cached data to local state (for editing)
  useEffect(() => {
    if (cachedBuildings && cachedBuildings.length > 0 && actionQueue.length === 0) {
      setBuildings((prev) => {
        // Prevent state update if data is identical
        if (JSON.stringify(prev) === JSON.stringify(cachedBuildings)) return prev;
        return cachedBuildings;
      });
      setIsLoading(false);
    }
  }, [cachedBuildings, actionQueue.length]);

  // Keep selectedBuildings in sync with updated buildings list
  useEffect(() => {
    if (selectedBuildings.length > 0 && buildings.length > 0) {
      const updatedSelected = selectedBuildings.map((selected) => {
        const found = buildings.find((b) => b.id === selected.id);
        return found ? { ...found } : selected;
      });
      if (JSON.stringify(updatedSelected) !== JSON.stringify(selectedBuildings)) {
        setSelectedBuildings(updatedSelected);
      }
    }
  }, [buildings]);

  // Sync hidden buildings
  useEffect(() => {
    if (cachedHiddenBuildings) {
      setHiddenBuildings(cachedHiddenBuildings);
    }
  }, [cachedHiddenBuildings]);

  useEffect(() => {
    const validIds = new Set(buildings.map((b) => b.id));
    setBuildingImageCache((prev) => {
      let changed = false;
      const next = {};

      for (const [id, url] of Object.entries(prev)) {
        if (validIds.has(id)) {
          next[id] = url;
        } else {
          changed = true;
          if (typeof url === "string" && url.startsWith("blob:")) {
            URL.revokeObjectURL(url);
          }
        }
      }

      return changed ? next : prev;
    });
  }, [buildings]);

  useEffect(() => {
    let cancelled = false;

    const loadBuildingImages = async () => {
      if (!(activeSubTab === "Dashboard" && dashboardView === "Card View")) return;

      for (const building of buildings) {
        if (!building?.id) continue;
        if (buildingImageCacheRef.current[building.id]) continue;

        const mappedImage = resolveBuildingImageFromRow(building);
        if (mappedImage) {
          if (!cancelled) {
            setBuildingImageCache((prev) => (prev[building.id] ? prev : { ...prev, [building.id]: mappedImage }));
          }
          continue;
        }

        try {
          const res = await Building.get_photo(building.id);
          if (!res.ok) continue;

          const contentType = res.headers.get("content-type") || "";
          if (contentType.includes("application/json")) continue;

          const blob = await res.blob();
          if (!blob || blob.size === 0) continue;

          const objectUrl = URL.createObjectURL(blob);
          if (cancelled) {
            URL.revokeObjectURL(objectUrl);
            return;
          }

          setBuildingImageCache((prev) => {
            if (prev[building.id]) {
              URL.revokeObjectURL(objectUrl);
              return prev;
            }
            return { ...prev, [building.id]: objectUrl };
          });
        } catch (error) {
          console.warn("[BuildingDashboard] Failed to load building image", building.id, error);
        }
      }
    };

    loadBuildingImages();

    return () => {
      cancelled = true;
    };
  }, [buildings, activeSubTab, dashboardView, resolveBuildingImageFromRow]);

  // Mount guard: skip the very first render so TanStack Query's own initial fetch
  // is not redundantly cancelled + re-fired by the refresh effect (Issue #7).
  const isFirstRefreshRender = useRef(true);

  // Handle manual refresh
  useEffect(() => {
    if (isFirstRefreshRender.current) {
      isFirstRefreshRender.current = false;
      return;
    }
    if (refresh !== undefined) {
      invalidateDashboard();
      refetchBuildings();
    }
  }, [refresh]);

  // SAVE FUNCTION (handles all queued actions)
  // Wrapped in useCallback to avoid stale closure over actionQueue and buildings.
  const save = useCallback(async () => {
    if (gridRef.current && gridRef.current.api) {
      gridRef.current.api.stopEditing();
    }

    setIsLoading(true);
    const id_map = {};
    const processedIds = new Set();

    try {
      // 1. Handle "new" actions first to get their backend IDs
      const newActions = actionQueue.filter((a) => a.action === "new");
      if (newActions.length > 0) {
        await Promise.all(
          newActions.map(async (actionItem) => {
            const { item } = actionItem;
            // Use latest data from state
            const latestItem = buildings.find((b) => b.id === item.id) || item;
            const res = await Building.new(latestItem);
            const data = await res.json();
            id_map[item.id] = data.id;
            processedIds.add(item.id);
          })
        );
      }

      // 2. Handle other actions in parallel
      const otherPromises = [];
      const updateActions = actionQueue.filter((a) => a.action === "update");
      const deleteActions = actionQueue.filter((a) => a.action === "delete");
      const archiveActions = actionQueue.filter((a) => a.action === "archive");
      const duplicateActions = actionQueue.filter((a) => a.action === "duplicate");
      const requestValidationActions = actionQueue.filter((a) => a.action === "request_validation");

      // Updates
      updateActions.forEach((actionItem) => {
        const { item } = actionItem;
        if (processedIds.has(item.id)) return;

        const { gwp, biogenic_gwp, ...cleanItem } = item;
        const updatedItem = {
          ...cleanItem,
          id: id_map[item.id] || item.id,
          category: cleanItem.building_category || cleanItem.category,
          Year: cleanItem.year ? parseInt(cleanItem.year, 10) : null,
        };
        otherPromises.push(Building.update(updatedItem));
      });

      // Batch deletes/archives/duplicates/validations
      if (deleteActions.length > 0) {
        const allDeleteIds = deleteActions.flatMap((a) => a.ids.map((id) => id_map[id] || id));
        otherPromises.push(Building.delete(allDeleteIds));
      }
      if (archiveActions.length > 0) {
        const allArchiveIds = archiveActions.flatMap((a) => a.ids.map((id) => id_map[id] || id));
        otherPromises.push(Building.archive(allArchiveIds));
      }
      if (duplicateActions.length > 0) {
        const allDuplicateIds = duplicateActions.flatMap((a) => a.ids.map((id) => id_map[id] || id));
        otherPromises.push(Building.duplicate(allDuplicateIds));
      }

      await Promise.all(otherPromises);

      // 3. Update local state with real IDs for new items
      if (Object.keys(id_map).length > 0) {
        setBuildings((prev) => prev.map((b) => (id_map[b.id] ? { ...b, id: id_map[b.id] } : b)));
        setSelectedBuildings((prev) => prev.map((b) => (id_map[b.id] ? { ...b, id: id_map[b.id] } : b)));
      }

      // 4. Finalize state
      setActionQueue([]);
      setIsSaveVisible(false);
      setIsSaveClicked(false);

      // Only refresh from server if complex actions like duplicate occurred (to get their new IDs/data)
      if (duplicateActions.length > 0) {
        setRefresh((r) => !r);
      } else {
        setIsLoading(false);
      }
    } catch (err) {
      console.error("Critical error during save:", err);
      setIsLoading(false);
      setIsSaveClicked(false);
      // Fallback: refresh everything if save failed to ensure consistency
      setRefresh((r) => !r);
    }
  }, [actionQueue, buildings, setIsLoading, setIsSaveVisible, setIsSaveClicked, setRefresh, setBuildings, setSelectedBuildings]);

  // Trigger save when button clicked (from context)
  // Include `save` (which captures actionQueue via useCallback) in deps to avoid stale closure.
  useEffect(() => {
    if (isSaveClicked) {
      if (actionQueue.length > 0) {
        save();
      } else {
        // Even if no queue, reset the state
        setIsSaveClicked(false);
        setIsLoading(false);
      }
    }
  }, [isSaveClicked, save]);

  // Focus on newly created building
  useEffect(() => {
    if (!focusedBuilding || !focusedBuilding.id || !gridRef.current) return;
    setTimeout(() => {
      const node = gridRef.current.api.getRowNode(focusedBuilding.id);
      if (node) {
        gridRef.current.api.ensureIndexVisible(node.rowIndex, "top");
        gridRef.current.api.setFocusedCell(node.rowIndex, "name");
        node.setSelected(true);
        // Automatically start editing the building name
        gridRef.current.api.startEditingCell({ rowIndex: node.rowIndex, colKey: "name" });
      }
      setFocusedBuilding();
    }, 50);
  }, [buildings, focusedBuilding]);

  // Search functionality
  useEffect(() => {
    if (gridRef.current && gridRef.current.api) {
      gridRef.current.api.setGridOption("quickFilterText", searchText);
    }
  }, [searchText]);

  // Calculate last update time


  // ACTION HANDLERS
  const deleteBuilding = (selectedBuildings) => {
    const ids = selectedBuildings.map((s) => s.id);
    setActionQueue((q) => [...q, { action: "delete", ids }]);
    setBuildings((p) => p.filter((building) => !ids.includes(building.id)));
    setIsSaveVisible(true);
    enqueueAlert({ message: t("Building deleted successfully") });
  };

  const newBuilding = () => {
    const newB = { ...defaultNewBuilding, id: uuidv4() };
    setBuildings((p) => [...p, newB]);
    setActionQueue((q) => [...q, { action: "new", item: newB }]);
    setFocusedBuilding(newB);
    setIsSaveVisible(true);
    enqueueAlert({ message: t("Building created successfully") });
  };

  const archiveBuilding = (selectedBuildings) => {
    const ids = selectedBuildings.map((s) => s.id);
    setActionQueue((q) => [...q, { action: "archive", ids }]);
    setBuildings((p) => p.filter((building) => !ids.includes(building.id)));
    setIsSaveVisible(true);
    enqueueAlert({ message: t("Building archived successfully") });
  };

  const hideBuildings = async (selectedBuildings) => {
    const ids = selectedBuildings.map((s) => s.id);
    try {
      await Building.hide(ids);
      setRefresh((prev) => !prev);
    } catch (error) {
      console.error("Error hiding buildings:", error);
    }
  };

  const unhideBuildings = async (ids) => {
    try {
      await Building.unhide(ids);
      setRefresh((prev) => !prev);
    } catch (error) {
      console.error("Error unhiding buildings:", error);
    }
  };

  const duplicateBuilding = (selectedBuildings) => {
    const ids = selectedBuildings.map((s) => s.id);
    setActionQueue((q) => [...q, { action: "duplicate", ids }]);
    setIsSaveVisible(true);
    enqueueAlert({ message: t("Building duplicated successfully") });
  };

  const requestValidation = async (selectedBuildings) => {
    const ids = selectedBuildings.map((s) => s.id);
    try {
      await Building.request_validation(ids);
      enqueueAlert({ message: t("Validation requested successfully"), severity: "success" });
      setRefresh((prev) => !prev);
    } catch (error) {
      console.error("Error requesting validation:", error);
      enqueueAlert({ message: t("Failed to request validation"), severity: "error" });
    }
  };

  const onSelectionChanged = (event) => {
    const selected = event.api.getSelectedRows();
    setSelectedBuildings(selected);
    setSelectedBuildingIds(selected.map((s) => s.id));
  };

  const handleContextMenu = useCallback((e) => {
    e.preventDefault();
    const container = gridRef.current?.eGridDiv || gridHeaderContainerRef.current?.closest('.MuiPaper-root');
    const xPos = container ? container.getBoundingClientRect().left : e.clientX;
    setMenuPosition({ x: xPos, y: e.clientY });
    setMenuVisible(true);
  }, [gridRef]);

  const handleCloseMenu = () => {
    setMenuVisible(false);
  };

  const openDialog = () => {
    if (dialogRef) {
      dialogRef.current.open();
    }
  };

  const contextMenuOptions = [
    { label: t("Duplicate"), action: duplicateBuilding, icon: <FiCopy />, needSelectedItems: true },
    { label: t("Intra Company"), action: archiveBuilding, icon: <FiLayers />, needSelectedItems: true },
    { label: t("Delete"), action: openDialog, icon: <FiTrash2 />, needSelectedItems: true },
    { type: 'divider' },
    { label: t("Request Validation"), action: requestValidation, icon: <FiCheckCircle />, needSelectedItems: true },
  ];

  // COLUMN DEFINITIONS
  const colDefs = useMemo(() => {
    const isHidden = (field) => !columnVisibilities[colVisibility_key]?.includes(field);

    const isEditable = (params) => {
      const status = params.data?.validation_status;
      return status !== "Pending" && status !== "Validated";
    };

    const onCellValueChanged = (params) => {
      const { data } = params;
      const now = new Date().toString().split(" GMT")[0];
      const updatedRow = data;
      setBuildings((prev) => prev.map((row) => (row.id === updatedRow.id ? { ...updatedRow, date_modified: now } : row)));
      setActionQueue((q) => [...q, { action: "update", item: updatedRow }]);
      setIsSaveVisible(true);
    };

    return [
      {
        headerName: "Name",
        field: "name",
        cellEditor: "agTextCellEditor",
        cellStyle: {
          fontWeight: 500,
          ...leftAlignColDef.cellStyle,
        },
        ...defaultColDef,
        editable: isEditable,
        onCellValueChanged,
        hide: isHidden("name"),
        headerCheckboxSelection: true,
      },
      {
        headerName: "Building Category",
        field: "building_category",
        cellEditor: "agSelectCellEditor",
        cellEditorParams: {
          values: buildingCategories,
        },
        ...defaultColDef,
        ...leftAlignColDef,
        editable: isEditable,
        onCellValueChanged,
        hide: isHidden("building_category"),
      },
      {
        headerName: "Location",
        field: "location",
        cellEditor: "agTextCellEditor",
        ...defaultColDef,
        ...leftAlignColDef,
        editable: isEditable,
        onCellValueChanged,
        hide: isHidden("location"),
      },
      {
        headerName: "Description",
        field: "description",
        cellEditor: "agTextCellEditor",
        ...defaultColDef,
        ...leftAlignColDef,
        editable: isEditable,
        onCellValueChanged,
        hide: isHidden("description"),
      },
      {
        headerName: "Year",
        field: "year",
        cellEditor: "agTextCellEditor",
        onCellValueChanged,
        ...defaultColDef,
        ...centerAlignColDef,
        editable: isEditable,
        hide: isHidden("year"),
      },
      {
        headerName: "GWP (kg CO₂e)",
        field: "gwp",
        ...rightAlignColDef,
        ...defaultColDef,
        ...floatFormater,
        hide: isHidden("gwp"),
      },
      {
        headerName: "Biogenic GWP (kg CO₂e)",
        field: "biogenic_gwp",
        ...rightAlignColDef,
        ...defaultColDef,
        ...floatFormater,
        hide: isHidden("biogenic_gwp"),
      },
      {
        headerName: "Cost ($)",
        field: "cost",
        cellEditor: "agTextCellEditor",
        onCellValueChanged,
        ...rightAlignColDef,
        ...defaultColDef,
        ...floatFormater,
        editable: isEditable,
      },
      { 
        headerName: "Cost per kg CO₂e ($/kg)", 
        colId: "cost_per_gwp", 
        valueGetter: (params) => {
          if (!params.data || !params.data.gwp || params.data.gwp === 0) return null;
          return Number(params.data.cost || 0) / Number(params.data.gwp);
        },
        ...rightAlignColDef, 
        ...defaultColDef, 
        ...floatFormater,
        editable: false
      },
      {
        headerName: "Publish Template",
        field: "is_published_template",
        ...checkBoxColDef,
        onCellClicked: (params) => {
          if (params.data?.validation_status === "Pending" || params.data?.validation_status === "Validated") return;
          const { colDef } = params;
          const updatedRow = { ...params.data, [colDef.field]: !params.value };
          const now = new Date().toString().split(" GMT")[0];
          updatedRow.date_modified = now;
          setBuildings((prev) => prev.map((row) => (row.id === updatedRow.id ? updatedRow : row)));
          setActionQueue((q) => [...q, { action: "update", item: updatedRow }]);
          setIsSaveVisible(true);
        },
        hide: !isAdmin || isHidden("is_published_template"),
      },
      { headerName: t("Validation Status"), field: "validation_status", ...defaultColDef, ...leftAlignColDef, hide: isHidden("validation_status") },
      { headerName: t("Date Modified"), field: "date_modified", ...defaultColDef, ...leftAlignColDef, hide: isHidden("date_modified") },
    ];
  }, [columnVisibilities, isAdmin]);

  // No changes needed here, handled by merged effect above

  // Reset states on refresh
  useEffect(() => {
    setIsSaveVisible(false);
    setIsLoading(false);
    setIsSaveClicked(false);
  }, [refresh]);

  const memoizedFooterLeftContent = useMemo(() => (
    <Box sx={{ display: 'flex', alignItems: 'center', ml: -1 }}>
      <CheckBox
        checked={globalView}
        onChange={(e) => setGlobalView(e.target.checked)}
        label="Global View"
      />
    </Box>
  ), [globalView]);

  const subTabs = [
    { label: t("Your Buildings"), value: "Your Buildings" },
    { label: t("Template Library"), value: "Template Library" },
  ];

  return (
    <MainContainer>
      {/* GWP CARDS - Permanent at the very top */}
      <Box sx={{ mb: 4 }}>
        <Box className="eng-flex-grid">
          {isLoading ? (
            Array.from(new Array(4)).map((_, i) => (
              <Box className="eng-metric-item" key={i}>
                <Paper sx={{ p: 2, width: 1, height: '140px', borderRadius: '20px', border: 1, borderColor: 'divider' }}>
                  <Skeleton variant="circular" width={50} height={50} sx={{ mb: 2 }} />
                  <Skeleton width="60%" height={32} />
                  <Skeleton width="40%" />
                </Paper>
              </Box>
            ))
          ) : (
            METRIC_CONFIG.map((m, i) => {
              const { title, ...otherProps } = m;
              return (
                <Box className="eng-metric-item" key={m.key}>
                  <MetricCard
                    {...otherProps}
                    title={t(title)}
                    value={gwpMetrics[m.key] || 0}
                    forceTons={anyMetricReachesTons}
                    delay={i * 0.07}
                  />
                </Box>
              );
            })
          )}
        </Box>
      </Box>

      {/* LIFE CYCLE STAGES - Permanent below metrics */}
      <Box sx={{ mb: 4 }}>
        <Typography sx={{ fontFamily: "Poppins", fontSize: "20px", fontWeight: 500, color: isDark ? "#fff" : "#0f172a", mb: 2, textAlign: 'left' }}>
          {isLoading ? <Skeleton width={150} /> : t("Life Cycle Stages")}
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: ORG_PALETTE.spacing.sectionGap }}>
          {isLoading ? (
            Array.from(new Array(6)).map((_, i) => (
              <Paper key={i} sx={{ p: '12px 14px', width: 1, height: '80px', borderRadius: tok.radius.card, border: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Skeleton variant="rounded" width={36} height={36} />
                <Box sx={{ flexGrow: 1 }}>
                  <Skeleton width="60%" height={16} />
                  <Skeleton width="40%" height={20} sx={{ mt: '4px' }} />
                </Box>
              </Paper>
            ))
          ) : (
            STAGE_CONFIG.map((s, i) => {
              const { title, subtitle, ...otherProps } = s;
              return (
                <CompactStageCard
                  key={s.key}
                  {...otherProps}
                  title={t(title)}
                  subtitle={t(subtitle)}
                  value={stageMetrics[s.key] || 0}
                  delay={(i + 4) * 0.07}
                  isDark={isDark}
                  tok={tok}
                  forceTons={anyStageReachesTons}
                />
              );
            })
          )}
        </Box>
      </Box>

      {/* TABS SWITCHER - Located directly below Lifecycle stages at 100% width */}
      <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 3 }}>
        <Box sx={{ width: "100%" }}>
          <SegmentedTabs
            options={[
              { label: t("Dashboard"), value: "Dashboard" },
              { label: t("Template Library"), value: "Template Library" },
              { label: t("Validations"), value: "Validations" },
            ]}
            selected={activeSubTab}
            setSelected={setActiveSubTab}
          />
        </Box>
      </Box>

      {/* VIEW 1: DASHBOARD */}
      {activeSubTab === "Dashboard" && (
        <Box sx={{ animation: "org-fadeInUp 0.4s ease", display: "flex", flexDirection: "column" }}>
          <Paper variant="Table Container" sx={{ mt: 1 }} className={!isLoading ? "stagger-load-item stagger-delay-6" : ""}>
            <TableHeaderContainer>
              <TableTitleContainer>
                <Typography variant="Table Title">
                  {isLoading ? <Skeleton width={120} /> : `${t("Buildings")} (${buildings.length})`}
                </Typography>
              </TableTitleContainer>
              <TableHeaderToolsContainer ref={gridHeaderContainerRef}>
                <SearchBox text={searchText} setText={setSearchText} placeholder={t("Search for buildings...")} />
                {dashboardView === "Grid View" && <ColumnVisibilityButton onClick={() => setColumnMenuVisible((p) => !p)} />}
                <AddButton onClick={newBuilding} />
                <ExportAsXLSXButton data={buildings} label="Export as .xlsx" filename="buildings.xlsx" />
                <HideButton onClick={() => hideBuildings(selectedBuildings)} disabled={selectedBuildings.length === 0} />
                <UnhideButton onClick={() => setUnhideDialogOpen(true)} hiddenCount={hiddenBuildings.length} />
                <RefreshButton />
                
                {/* View Switcher Toggle */}
                <Box sx={{ display: 'flex', border: '1px solid', borderColor: tok.border.divider, borderRadius: '8px', overflow: 'hidden', ml: 1.5, height: '40px' }}>
                  <Button 
                    onClick={() => setDashboardView("Grid View")} 
                    sx={{ 
                      borderRadius: 0, 
                      textTransform: 'none',
                      fontFamily: 'Poppins',
                      fontWeight: 600,
                      fontSize: '13px',
                      px: 2,
                      bgcolor: dashboardView === "Grid View" ? "rgba(101, 157, 51, 0.12)" : "transparent",
                      color: dashboardView === "Grid View" ? "#659d33" : (isDark ? "#94a3b8" : "#64748b"),
                      '&:hover': { bgcolor: "rgba(101, 157, 51, 0.2)" }
                    }}
                  >
                    {t("Grid View")}
                  </Button>
                  <Button 
                    onClick={() => setDashboardView("Card View")} 
                    sx={{ 
                      borderRadius: 0, 
                      textTransform: 'none',
                      fontFamily: 'Poppins',
                      fontWeight: 600,
                      fontSize: '13px',
                      px: 2,
                      borderLeft: '1px solid',
                      borderColor: tok.border.divider,
                      bgcolor: dashboardView === "Card View" ? "rgba(101, 157, 51, 0.12)" : "transparent",
                      color: dashboardView === "Card View" ? "#659d33" : (isDark ? "#94a3b8" : "#64748b"),
                      '&:hover': { bgcolor: "rgba(101, 157, 51, 0.2)" }
                    }}
                  >
                    {t("Card View")}
                  </Button>
                </Box>
              </TableHeaderToolsContainer>
            </TableHeaderContainer>
            <Box sx={{ position: 'relative', p: dashboardView === "Card View" ? 3 : 0 }}>
              {isLoading && (
                <Box className="skeleton-shimmer" sx={{ position: 'absolute', inset: 0, zIndex: 1, pointerEvents: 'none', opacity: 0.1 }} />
              )}
              {dashboardView === "Grid View" ? (
                <AgGrid
                  ref={gridRef}
                  columnDefs={colDefs}
                  rowData={buildings}
                  onSelectionChanged={onSelectionChanged}
                  handleContextMenu={handleContextMenu}
                  colVisibility_key={colVisibility_key}
                  footerLeftContent={memoizedFooterLeftContent}
                />
              ) : (
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 3 }}>
                  {buildings
                    .filter(b => (b.name || "").toLowerCase().includes(searchText.toLowerCase()))
                    .map((building) => (
                      <BuildingCard
                        key={building.id}
                        building={building}
                        resolvedImage={buildingImageCache[building.id]}
                        onClick={() => handleOpenBuilding(building)}
                        isDark={isDark}
                        tok={tok}
                        onMenuClick={(e) => handleCardMenuClick(e, building)}
                      />
                    ))}
                </Box>
              )}
            </Box>
          </Paper>

          {/* GLOBAL VIEW */}
          {globalView && (
            <GlobalViewContainer
              buildings={buildings}
              selectedBuildings={selectedBuildings}
              aggregatedData={aggregatedData}
              isLoading={isAggregationLoading}
            />
          )}
        </Box>
      )}

      {/* VIEW 2: TEMPLATE LIBRARY */}
      {activeSubTab === "Template Library" && (
        <Box sx={{ animation: "org-fadeInUp 0.4s ease", display: "flex", flexDirection: "column", mt: 1 }}>
          <Paper variant="Table Container" sx={{ mt: 1 }}>
            <TableHeaderContainer>
              <TableTitleContainer>
                <Typography variant="Table Title">
                  {t("Template Library")}
                </Typography>
              </TableTitleContainer>
              <TableHeaderToolsContainer>
                <SearchBox text={searchText} setText={setSearchText} placeholder={t("Search templates...")} />
                
                {/* View Switcher Toggle */}
                <Box sx={{ display: 'flex', border: '1px solid', borderColor: tok.border.divider, borderRadius: '8px', overflow: 'hidden', height: '40px' }}>
                  <Button 
                    onClick={() => setTemplateView("Grid View")} 
                    sx={{ 
                      borderRadius: 0, 
                      textTransform: 'none',
                      fontFamily: 'Poppins',
                      fontWeight: 600,
                      fontSize: '13px',
                      px: 2,
                      bgcolor: templateView === "Grid View" ? "rgba(101, 157, 51, 0.12)" : "transparent",
                      color: templateView === "Grid View" ? "#659d33" : (isDark ? "#94a3b8" : "#64748b"),
                      '&:hover': { bgcolor: "rgba(101, 157, 51, 0.2)" }
                    }}
                  >
                    {t("Grid View")}
                  </Button>
                  <Button 
                    onClick={() => setTemplateView("Card View")} 
                    sx={{ 
                      borderRadius: 0, 
                      textTransform: 'none',
                      fontFamily: 'Poppins',
                      fontWeight: 600,
                      fontSize: '13px',
                      px: 2,
                      borderLeft: '1px solid',
                      borderColor: tok.border.divider,
                      bgcolor: templateView === "Card View" ? "rgba(101, 157, 51, 0.12)" : "transparent",
                      color: templateView === "Card View" ? "#659d33" : (isDark ? "#94a3b8" : "#64748b"),
                      '&:hover': { bgcolor: "rgba(101, 157, 51, 0.2)" }
                    }}
                  >
                    {t("Card View")}
                  </Button>
                </Box>
              </TableHeaderToolsContainer>
            </TableHeaderContainer>

            <Box sx={{ position: 'relative', p: templateView === "Card View" ? 3 : 0 }}>
              {templateView === "Card View" ? (
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 3 }}>
                  {MURB_TEMPLATES
                    .filter(t => t.name.toLowerCase().includes(searchText.toLowerCase()))
                    .map((template) => (
                      <BuildingCard
                        key={template.id}
                        building={template}
                        onAction={handleUseTemplate}
                        actionLabel={t("Use Template")}
                        isTemplate={true}
                        isDark={isDark}
                        tok={tok}
                        isSelected={selectedTemplateId === template.id}
                        onClick={() => setSelectedTemplateId(prev => prev === template.id ? null : template.id)}
                      />
                    ))}
                </Box>
              ) : (
                <AgGrid
                  columnDefs={colDefs}
                  rowData={MURB_TEMPLATES.filter(t => t.name.toLowerCase().includes(searchText.toLowerCase()))}
                  onSelectionChanged={() => {}}
                  handleContextMenu={() => {}}
                  colVisibility_key="template_library_column_visibility"
                />
              )}
            </Box>
          </Paper>
        </Box>
      )}

      {/* VIEW 3: VALIDATIONS */}
      {activeSubTab === "Validations" && (
        <Box sx={{ animation: "org-fadeInUp 0.4s ease", display: "flex", flexDirection: "column", mt: 1 }}>
          <Paper variant="Table Container" sx={{ mt: 1 }}>
            <TableHeaderContainer>
              <TableTitleContainer>
                <Typography variant="Table Title">
                  {validationSubTab === "Pending" ? t("Pending Validations") : t("Validated Buildings")}
                </Typography>
              </TableTitleContainer>
              <TableHeaderToolsContainer>
                <SearchBox text={searchText} setText={setSearchText} placeholder={t("Search validations...")} />
                
                {/* Validation Status Dropdown */}
                <FormControl size="small" sx={{ minWidth: 140, ml: 1.5 }}>
                  <Select
                    value={validationSubTab}
                    onChange={(e) => setValidationSubTab(e.target.value)}
                    sx={{
                      height: '40px',
                      borderRadius: '8px',
                      fontFamily: 'Poppins',
                      fontWeight: 600,
                      fontSize: '13px',
                      color: '#659d33',
                      bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'transparent',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: tok.border.divider,
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#659d33',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#659d33',
                      }
                    }}
                  >
                    <MenuItem value="Pending" sx={{ fontFamily: 'Poppins', fontSize: '13px', fontWeight: 500 }}>{t("Pending")}</MenuItem>
                    <MenuItem value="Validated" sx={{ fontFamily: 'Poppins', fontSize: '13px', fontWeight: 500 }}>{t("Validated")}</MenuItem>
                  </Select>
                </FormControl>
                
                {/* View Switcher Toggle */}
                <Box sx={{ display: 'flex', border: '1px solid', borderColor: tok.border.divider, borderRadius: '8px', overflow: 'hidden', ml: 1.5, height: '40px' }}>
                  <Button 
                    onClick={() => setValidationView("Grid View")}
                    sx={{ 
                      borderRadius: 0, 
                      textTransform: 'none',
                      fontFamily: 'Poppins',
                      fontWeight: 600,
                      fontSize: '13px',
                      px: 2,
                      bgcolor: validationView === "Grid View" ? "rgba(101, 157, 51, 0.12)" : "transparent",
                      color: validationView === "Grid View" ? "#659d33" : (isDark ? "#94a3b8" : "#64748b"),
                      '&:hover': { bgcolor: "rgba(101, 157, 51, 0.2)" }
                    }}
                  >
                    {t("Grid View")}
                  </Button>
                  <Button 
                    onClick={() => setValidationView("Card View")}
                    sx={{ 
                      borderRadius: 0, 
                      textTransform: 'none',
                      fontFamily: 'Poppins',
                      fontWeight: 600,
                      fontSize: '13px',
                      px: 2,
                      borderLeft: '1px solid',
                      borderColor: tok.border.divider,
                      bgcolor: validationView === "Card View" ? "rgba(101, 157, 51, 0.12)" : "transparent",
                      color: validationView === "Card View" ? "#659d33" : (isDark ? "#94a3b8" : "#64748b"),
                      '&:hover': { bgcolor: "rgba(101, 157, 51, 0.2)" }
                    }}
                  >
                    {t("Card View")}
                  </Button>
                </Box>
              </TableHeaderToolsContainer>
            </TableHeaderContainer>

            <Box sx={{ position: 'relative', p: validationView === "Card View" ? 3 : 0 }}>
              {validationView === "Grid View" ? (
                <AgGrid
                  columnDefs={colDefs}
                  rowData={buildings.filter(b => {
                    const status = b.validation_status || "Not Validated";
                    return status === validationSubTab && (b.name || "").toLowerCase().includes(searchText.toLowerCase());
                  })}
                  onSelectionChanged={onSelectionChanged}
                  handleContextMenu={handleContextMenu}
                  colVisibility_key="building_validation_column_visibility"
                />
              ) : (
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 3 }}>
                  {buildings
                    .filter(b => {
                      const status = b.validation_status || "Not Validated";
                      return status === validationSubTab && (b.name || "").toLowerCase().includes(searchText.toLowerCase());
                    })
                    .map((building) => (
                      <BuildingCard
                        key={building.id}
                        building={building}
                        resolvedImage={buildingImageCache[building.id]}
                        onClick={() => handleOpenBuilding(building)}
                        isDark={isDark}
                        tok={tok}
                        onMenuClick={(e) => handleCardMenuClick(e, building)}
                      />
                    ))}
                </Box>
              )}
            </Box>
          </Paper>
        </Box>
      )}

      {/* CONTEXT MENUS & DIALOGS */}
      <ColumnMenu
        anchorRef={gridHeaderContainerRef}
        visible={columnMenuVisible}
        onClose={() => setColumnMenuVisible(false)}
        colDefs={colDefs}
        colVisibility_key={colVisibility_key}
        defaultCols={defaultCols}
        gridRef={gridRef}
      />
      <ContextMenu x={menuPosition.x} y={menuPosition.y} visible={menuVisible} onClose={handleCloseMenu} selectedItems={selectedBuildings} options={contextMenuOptions} />
      <DialogDelete selectedItems={selectedBuildings} handleDelete={deleteBuilding} ref={dialogRef} />
      <UnhideDialog open={unhideDialogOpen} onClose={() => setUnhideDialogOpen(false)} hiddenItems={hiddenBuildings} onUnhide={unhideBuildings} entityType="Buildings" />
      <SaveButton />
    </MainContainer>
  );
};

export default BuildingDashboard;
